/**
 * The pictures a section stands in with while the merchant has none of its own.
 *
 * Every image here is an SVG written at call time and handed back as a data
 * URI, which is what makes this safe to put behind a merchant's half-finished
 * page: nothing is fetched, nothing is hosted, nothing can 404 in front of
 * someone deciding whether to pay for the plan. An `<img>` is an `<img>`, so
 * the section renders through exactly the markup a real photograph renders
 * through - the same `object-cover`, the same aspect ratio, the same rounding -
 * and swaps back to the real one the moment an upload lands.
 *
 * They are drawn rather than greyed. A skeleton loader says "this is broken and
 * we are hiding it"; an illustration says "a picture goes here, about this
 * shape, this heavy". The second is the only one a merchant can design against,
 * and it is the difference between a customiser that reads as a website builder
 * and one that reads as a form.
 *
 * The palettes are deliberately the artwork's own rather than the theme's. An
 * `<img>` cannot read a CSS custom property, so a themed placeholder would mean
 * re-encoding every picture on every keystroke in the colour picker - and a
 * stand-in tinted exactly like the shop is one a merchant stops seeing as a
 * stand-in. These are quiet, warm and obviously illustrations, which is what
 * they should be.
 */

/** What a slot wants a picture *of* - not what shape it wants it in. */
export type PlaceholderArt = 'product' | 'lifestyle' | 'collection' | 'portrait' | 'article';

interface Palette {
  /** The ground, top to bottom. */
  from: string;
  to: string;
  /** The subject. */
  ink: string;
  /** The one warm note in the composition. */
  accent: string;
}

/**
 * Six duotones, rotated by seed.
 *
 * Enough that a grid of eight cards does not read as one picture repeated, few
 * enough that the grid still reads as one shop. They are all low-chroma and
 * mid-light on purpose: a placeholder that shouts is a placeholder that decides
 * what the merchant's page looks like.
 */
const PALETTES: readonly Palette[] = [
  { from: '#f4e9dc', to: '#e3cfb8', ink: '#8a6a4f', accent: '#c08457' },
  { from: '#e9f0e9', to: '#cbdccd', ink: '#4f6b55', accent: '#7da37f' },
  { from: '#e9edf3', to: '#ccd6e4', ink: '#4a5a72', accent: '#6e8cb4' },
  { from: '#f8e9ed', to: '#eacad4', ink: '#7a4f5c', accent: '#c4808f' },
  { from: '#fbf1de', to: '#f2dcb5', ink: '#7a5c2e', accent: '#d2a054' },
  { from: '#e3eff3', to: '#c0dce6', ink: '#3f626e', accent: '#6fa3b4' },
];

/** Wraps a scene in its frame and its ground. */
function scene(width: number, height: number, palette: Palette, body: string): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="0.35" y2="1">` +
    `<stop offset="0" stop-color="${palette.from}"/>` +
    `<stop offset="1" stop-color="${palette.to}"/>` +
    `</linearGradient></defs>` +
    `<rect width="${width}" height="${height}" fill="url(#g)"/>` +
    body +
    `</svg>`
  );
}

/**
 * The five things a shop photographs, as silhouettes.
 *
 * Objects rather than boxes, because the point of the picture is to tell a
 * merchant "your product stands here, about this big" - and a rectangle inside
 * a rectangle tells them nothing the border had not already.
 */
const PRODUCT_SUBJECTS: readonly ((palette: Palette) => string)[] = [
  // A bottle.
  ({ ink, accent }) =>
    `<rect x="372" y="188" width="56" height="56" rx="16" fill="${ink}" opacity="0.55"/>` +
    `<rect x="382" y="238" width="36" height="64" fill="${ink}" opacity="0.7"/>` +
    `<rect x="316" y="292" width="168" height="300" rx="52" fill="${ink}" opacity="0.88"/>` +
    `<rect x="316" y="392" width="168" height="96" fill="${accent}" opacity="0.85"/>`,
  // A shaded lamp.
  ({ ink, accent }) =>
    `<circle cx="400" cy="336" r="52" fill="${accent}" opacity="0.5"/>` +
    `<path d="M300 302 L500 302 L468 176 L332 176 Z" fill="${ink}" opacity="0.88"/>` +
    `<rect x="392" y="302" width="16" height="238" fill="${ink}" opacity="0.7"/>` +
    `<ellipse cx="400" cy="548" rx="92" ry="22" fill="${ink}" opacity="0.8"/>`,
  // A tote.
  ({ ink, accent }) =>
    `<path d="M338 300 q62 -112 124 0" fill="none" stroke="${ink}" stroke-width="18" opacity="0.7"/>` +
    `<path d="M296 296 h208 l30 292 h-268 Z" fill="${ink}" opacity="0.88"/>` +
    `<rect x="300" y="420" width="208" height="56" fill="${accent}" opacity="0.8"/>`,
  // A stack of boxes.
  ({ ink, accent }) =>
    `<rect x="286" y="332" width="228" height="150" rx="18" fill="${ink}" opacity="0.85"/>` +
    `<rect x="322" y="198" width="156" height="140" rx="18" fill="${accent}" opacity="0.85"/>` +
    `<rect x="392" y="198" width="16" height="140" fill="${ink}" opacity="0.35"/>`,
  // A mug.
  ({ ink, accent }) =>
    `<path d="M490 344 q68 12 68 72 q0 60 -68 72" fill="none" stroke="${ink}" stroke-width="26" opacity="0.8"/>` +
    `<path d="M310 300 h180 v190 q0 60 -60 60 h-60 q-60 0 -60 -60 Z" fill="${ink}" opacity="0.88"/>` +
    `<rect x="310" y="362" width="180" height="52" fill="${accent}" opacity="0.8"/>`,
];

function productScene(palette: Palette, index: number): string {
  return scene(
    800,
    800,
    palette,
    `<circle cx="646" cy="152" r="122" fill="#ffffff" opacity="0.22"/>` +
      `<ellipse cx="400" cy="624" rx="182" ry="26" fill="${palette.ink}" opacity="0.13"/>` +
      PRODUCT_SUBJECTS[index % PRODUCT_SUBJECTS.length](palette),
  );
}

/** A wide horizon, for the pictures a shop puts its words on top of. */
function lifestyleBody(palette: Palette): string {
  const { ink, accent } = palette;
  return (
    `<circle cx="1178" cy="266" r="124" fill="${accent}" opacity="0.55"/>` +
    `<path d="M0 618 q262 -178 522 -40 q240 128 458 -22 q302 -198 620 62 L1600 900 L0 900 Z" fill="${ink}" opacity="0.26"/>` +
    `<path d="M0 762 q300 -142 620 -22 q300 112 560 -40 q240 -128 420 42 L1600 900 L0 900 Z" fill="${ink}" opacity="0.5"/>`
  );
}

function lifestyleScene(palette: Palette): string {
  return scene(1600, 900, palette, lifestyleBody(palette));
}

/** A fanned stack, for a shelf of the shop rather than one thing on it. */
function collectionScene(palette: Palette, index: number): string {
  const { ink, accent } = palette;
  return scene(
    1200,
    900,
    palette,
    `<circle cx="1000" cy="180" r="150" fill="#ffffff" opacity="0.24"/>` +
      `<rect x="180" y="292" width="340" height="430" rx="36" fill="${ink}" opacity="0.32" transform="rotate(-10 350 507)"/>` +
      `<rect x="700" y="300" width="330" height="424" rx="36" fill="${ink}" opacity="0.5" transform="rotate(9 865 512)"/>` +
      `<rect x="418" y="248" width="376" height="472" rx="36" fill="${accent}" opacity="0.82"/>` +
      `<rect x="418" y="${index % 2 === 0 ? 440 : 396}" width="376" height="60" fill="${ink}" opacity="0.28"/>`,
  );
}

/** A person, at the distance a review is photographed from. */
function portraitScene(palette: Palette): string {
  const { ink, accent } = palette;
  return scene(
    600,
    600,
    palette,
    `<circle cx="300" cy="256" r="188" fill="${accent}" opacity="0.3"/>` +
      `<path d="M84 600 q0 -172 216 -172 q216 0 216 172 Z" fill="${ink}" opacity="0.82"/>` +
      `<circle cx="300" cy="238" r="112" fill="${ink}" opacity="0.9"/>`,
  );
}

/** An editorial cover, for the card above a headline. */
function articleScene(palette: Palette, index: number): string {
  const { ink, accent } = palette;
  return scene(
    1200,
    800,
    palette,
    `<circle cx="852" cy="266" r="196" fill="${accent}" opacity="0.62"/>` +
      `<path d="M0 800 L0 520 q300 -140 600 -20 q300 118 600 -40 L1200 800 Z" fill="${ink}" opacity="${
        index % 2 === 0 ? 0.42 : 0.3
      }"/>` +
      `<rect x="120" y="150" width="230" height="230" rx="24" fill="${ink}" opacity="0.28"/>`,
  );
}

/**
 * One picture, as a data URI.
 *
 * `seed` is what keeps a grid varied and stable at the same time: the fourth
 * card is always the fourth palette and the fourth silhouette, so re-rendering
 * on a keystroke does not reshuffle the merchant's page under them.
 */
export function placeholderImage(kind: PlaceholderArt, seed = 0): string {
  const index = Math.abs(Math.trunc(seed));
  const palette = PALETTES[index % PALETTES.length];

  const source =
    kind === 'product'
      ? productScene(palette, index)
      : kind === 'collection'
        ? collectionScene(palette, index)
        : kind === 'portrait'
          ? portraitScene(palette)
          : kind === 'article'
            ? articleScene(palette, index)
            : lifestyleScene(palette);

  return `data:image/svg+xml,${encodeURIComponent(source)}`;
}

// --- Festive art ------------------------------------------------------------

/**
 * The occasions the festival kits dress a shop for. See
 * `features/store/editor/kits/`.
 */
export type FestiveMotif = 'diwali' | 'holi' | 'eid' | 'rakhi' | 'wedding' | 'sale';

/**
 * The colours a festive picture is drawn in: the kit's own palette, because
 * unlike the stand-ins above these are meant to look like the look they
 * belong to - a Diwali card that is not gold and deep red is not a Diwali card.
 */
export interface FestiveColors {
  /** The ground, top to bottom. */
  from: string;
  to: string;
  /** The motif itself. */
  ink: string;
  /** Flames, sparks, the one bright note. */
  accent: string;
}

/** A row of lamps, each with its flame. */
function diwaliBody({ ink, accent }: FestiveColors): string {
  const lamps = [260, 560, 860, 1160, 1460]
    .map(
      (x, index) =>
        `<ellipse cx="${x}" cy="${690 - (index % 2) * 30}" rx="104" ry="46" fill="${ink}" opacity="0.9"/>` +
        `<path d="M${x - 104} ${676 - (index % 2) * 30} q104 96 208 0" fill="${ink}"/>` +
        `<path d="M${x} ${560 - (index % 2) * 30} q34 52 0 96 q-34 -44 0 -96 Z" fill="${accent}"/>` +
        `<circle cx="${x}" cy="${612 - (index % 2) * 30}" r="64" fill="${accent}" opacity="0.18"/>`,
    )
    .join('');
  const sparks = [
    [180, 160],
    [420, 260],
    [760, 120],
    [1040, 230],
    [1320, 140],
    [1500, 300],
  ]
    .map(([x, y]) => `<circle cx="${x}" cy="${y}" r="10" fill="${accent}" opacity="0.8"/>`)
    .join('');
  return sparks + lamps;
}

/** Colour thrown into the air. */
function holiBody({ ink, accent }: FestiveColors): string {
  const splashes: ReadonlyArray<readonly [number, number, number, string, number]> = [
    [320, 320, 210, accent, 0.75],
    [760, 520, 260, ink, 0.55],
    [1200, 300, 230, '#ec4899', 0.6],
    [1380, 680, 180, '#22c55e', 0.55],
    [260, 700, 160, '#3b82f6', 0.5],
    [980, 160, 120, '#facc15', 0.7],
  ];
  return splashes
    .map(
      ([x, y, r, fill, opacity]) =>
        `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}" opacity="${opacity}"/>` +
        `<circle cx="${x + r * 0.9}" cy="${y - r * 0.6}" r="${r * 0.18}" fill="${fill}" opacity="${opacity}"/>` +
        `<circle cx="${x - r * 0.8}" cy="${y + r * 0.7}" r="${r * 0.12}" fill="${fill}" opacity="${opacity}"/>`,
    )
    .join('');
}

/** A crescent, a star and a hanging lantern. */
function eidBody({ ink, accent, from }: FestiveColors): string {
  return (
    `<circle cx="1180" cy="300" r="170" fill="${accent}"/>` +
    `<circle cx="1250" cy="250" r="150" fill="${from}"/>` +
    `<path d="M1420 180 l18 40 44 4 -34 28 10 44 -38 -24 -38 24 10 -44 -34 -28 44 -4 Z" fill="${accent}"/>` +
    `<line x1="380" y1="0" x2="380" y2="300" stroke="${ink}" stroke-width="6"/>` +
    `<path d="M300 300 h160 l30 60 v220 l-30 60 h-160 l-30 -60 v-220 Z" fill="${ink}" opacity="0.9"/>` +
    `<rect x="330" y="380" width="100" height="180" rx="50" fill="${accent}" opacity="0.8"/>` +
    `<path d="M0 820 q400 -120 800 -20 q400 100 800 -40 L1600 900 L0 900 Z" fill="${ink}" opacity="0.35"/>`
  );
}

/** A thread across the picture, and the flower at its heart. */
function rakhiBody({ ink, accent }: FestiveColors): string {
  const petals = Array.from({ length: 10 }, (_, index) => {
    const angle = (index / 10) * Math.PI * 2;
    const x = 800 + Math.cos(angle) * 110;
    const y = 450 + Math.sin(angle) * 110;
    return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="62" fill="${accent}" opacity="0.85"/>`;
  }).join('');
  return (
    `<path d="M0 470 q400 -60 800 -20 q400 40 800 -30" fill="none" stroke="${ink}" stroke-width="18"/>` +
    `<path d="M0 500 q400 -60 800 -20 q400 40 800 -30" fill="none" stroke="${accent}" stroke-width="8" opacity="0.7"/>` +
    petals +
    `<circle cx="800" cy="450" r="92" fill="${ink}"/>` +
    `<circle cx="800" cy="450" r="44" fill="${accent}"/>`
  );
}

/** Marigold strings, hung the way they are over a doorway. */
function weddingBody({ ink, accent }: FestiveColors): string {
  const strings = [200, 520, 840, 1160, 1480]
    .map((x) =>
      Array.from(
        { length: 9 },
        (_, index) =>
          `<circle cx="${x}" cy="${40 + index * 46}" r="24" fill="${index % 3 === 2 ? ink : accent}" opacity="0.9"/>`,
      ).join(''),
    )
    .join('');
  return (
    `<path d="M0 60 q200 140 400 0 q200 140 400 0 q200 140 400 0 q200 140 400 0" fill="none" stroke="${accent}" stroke-width="30" opacity="0.85"/>` +
    strings +
    `<path d="M560 900 v-240 a240 240 0 0 1 480 0 v240" fill="none" stroke="${ink}" stroke-width="24" opacity="0.6"/>`
  );
}

/** A burst, the shape a sale sticker has always been. */
function saleBody({ ink, accent }: FestiveColors): string {
  const points = Array.from({ length: 32 }, (_, index) => {
    const angle = (index / 32) * Math.PI * 2;
    const radius = index % 2 === 0 ? 300 : 240;
    return `${(800 + Math.cos(angle) * radius).toFixed(1)},${(450 + Math.sin(angle) * radius).toFixed(1)}`;
  }).join(' ');
  return (
    `<circle cx="200" cy="160" r="120" fill="${ink}" opacity="0.15"/>` +
    `<circle cx="1420" cy="760" r="160" fill="${ink}" opacity="0.15"/>` +
    `<polygon points="${points}" fill="${accent}"/>` +
    `<circle cx="800" cy="450" r="190" fill="${ink}"/>` +
    `<path d="M720 540 l160 -180" stroke="${accent}" stroke-width="26" stroke-linecap="round"/>` +
    `<circle cx="728" cy="380" r="34" fill="${accent}"/>` +
    `<circle cx="872" cy="520" r="34" fill="${accent}"/>`
  );
}

const FESTIVE_BODIES: Record<FestiveMotif, (colors: FestiveColors) => string> = {
  diwali: diwaliBody,
  holi: holiBody,
  eid: eidBody,
  rakhi: rakhiBody,
  wedding: weddingBody,
  sale: saleBody,
};

/**
 * One festive picture, 16:9, as a data URI.
 *
 * Drawn, never photographed, for the reason the section presets give: a
 * merchant who publishes before noticing must not be selling under somebody
 * else's photograph. Used on the kit cards and in the kit's preview; the
 * document itself only takes a picture the merchant uploads or makes, because
 * the server keeps images to real addresses (see `ImageSrc` in
 * `app/schemas/store.py`).
 */
export function festiveImage(motif: FestiveMotif, colors: FestiveColors): string {
  const palette: Palette = { from: colors.from, to: colors.to, ink: colors.ink, accent: colors.accent };
  const source = scene(1600, 900, palette, FESTIVE_BODIES[motif](colors));
  return `data:image/svg+xml,${encodeURIComponent(source)}`;
}
